import { Inject, Injectable } from '@angular/core';

import { TrackerService } from '@services/tracker.service';
import { toError } from 'src/app/common/logging/error-normalization';
import makeDebug from '../../../../makeDebug';
import { IScheduler } from '../contracts/scheduler/scheduler';
import { IPipelineContext } from '../contracts/start/pipeline-context';
import { PipelineResult } from '../contracts/start/pipeline-element';
import { PlatformSyncDetector, SyncRegistry as Registry } from '../contracts/start/pipeline-endpoints';
import { SyncToken } from '../contracts/sync-token';
import { ISyncContext } from '../contracts/sync/sync-context';
import { ISyncElement } from '../contracts/sync/sync-element';
import { ISyncRegistry } from '../contracts/sync/sync-registry';
import { SyncTrigger } from '../scheduler/sync-trigger';
import { SyncOrderToken } from '../sync/sync-order';

const debug = makeDebug('sync:sync-registry');

const SYNC_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

// Errors already reported to Sentry by executeSync (with their step name). start() skips
// these so one failure produces exactly one Sentry event instead of two (work item 27022).
const reportedSyncErrors = new WeakSet<Error>();

/**
 * Orchestrates the execution of sync pipeline elements in sequence.
 *
 * This class is a singleton — instantiated via `syncRegistryFactory` in PipelineModule,
 * which caches the instance in a module-level variable. The same instance is provided
 * both as a PipelineToken (multi) and as the SyncRegistry injection token.
 */
@Injectable()
export class SyncRegistry implements ISyncRegistry {
  private static _syncRunCounter = 0;

  public get endpoint(): string {
    return Registry;
  }

  public get parent(): string {
    return PlatformSyncDetector;
  }

  private _context: ISyncContext = {
    id: `${Date.now()}`,
    params: { isStartUpSync: true },
  };

  private _isSyncRunning = false;
  private _pendingSync: IPipelineContext | null = null;
  private _currentSyncRunId: string | null = null;
  private _currentSyncStep: string | null = null;
  private _disabled = false;

  constructor(
    @Inject(SyncTrigger) private _syncScheduler: IScheduler,
    @Inject(SyncToken) private _syncElements: ISyncElement[],
    @Inject(SyncOrderToken) private _syncOrder: string[],
    private _trackerService: TrackerService
  ) {
    this.sortElementsByOrder();
  }

  public async execute(context: IPipelineContext): Promise<PipelineResult> {
    debug('registering the pipeline with the sync trigger');
    this._syncScheduler.schedule(this, context);

    return { contextId: context.id, scheduled: true, jobDone: true };
  }

  /**
   * !!! This disables the sync registry and will prevent any sync from running !!!
   * Only use this if you are manually reloading the app afterwards and want a sync to stop from interfering the process
   */
  public disable(): void {
    this._disabled = true;
    this._pendingSync = null;
    debug('sync disabled');
  }

  public async start(context: IPipelineContext): Promise<void> {
    if (this._disabled) {
      debug('start() blocked — sync is disabled');
      return;
    }
    const syncRunId = `sync-${++SyncRegistry._syncRunCounter}-${Date.now()}`;
    debug(`[${syncRunId}] start() called | isOnline=${context.params.isOnline}`);
    window.logger.addBreadcrumb({
      message: `[SyncRegistry][${syncRunId}] start | isOnline=${context.params.isOnline}`,
    });

    if (this._isSyncRunning) {
      debug(`[${syncRunId}] QUEUED (current running: ${this._currentSyncRunId})`);
      window.logger.addBreadcrumb({
        message: `[SyncRegistry][${syncRunId}] QUEUED (current: ${this._currentSyncRunId})`,
      });
      this._pendingSync = context;
      return;
    }

    this._isSyncRunning = true;
    this._currentSyncRunId = syncRunId;
    try {
      await Promise.race([this.executeSync(context, syncRunId), this.syncTimeout()]);
    } catch (error) {
      debug(`[${syncRunId}] FAILED`, error?.message);
      if (error instanceof Error && reportedSyncErrors.has(error)) {
        window.logger.addBreadcrumb({
          message: `[SyncRegistry][${syncRunId}] FAILED (already reported at step level): ${error.message}`,
        });
      } else {
        // Only failures executeSync could not attribute to a step end up here — in
        // practice the 15-minute timeout.
        window.logger.error('sync failed or timed out', toError(error, 'sync failed or timed out'));
      }
    } finally {
      debug(`[${syncRunId}] FINISHED`);
      window.logger.addBreadcrumb({ message: `[SyncRegistry][${syncRunId}] FINISHED` });
      this._isSyncRunning = false;
      this._currentSyncRunId = null;

      if (this._pendingSync) {
        const pendingContext = this._pendingSync;
        this._pendingSync = null;
        debug(`[${syncRunId}] executing pending sync`);
        window.logger.addBreadcrumb({ message: `[SyncRegistry][${syncRunId}] executing pending sync` });
        await this.start(pendingContext);
      }
    }
  }

  private syncTimeout(): Promise<never> {
    return new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error(`Sync timed out after 15 minutes (step: ${this._currentSyncStep ?? 'unknown'})`)),
        SYNC_TIMEOUT_MS
      )
    );
  }

  private async executeSync(context: IPipelineContext, syncRunId: string): Promise<void> {
    if (context.params.isOnline != null) {
      this._context.params.isOnline = context.params.isOnline;
    }

    if (context.params.showSyncIndicator != null) {
      this._context.params.isStartUpSync = context.params.showSyncIndicator;
    }

    this._context.syncRunId = syncRunId;
    debug(
      `[${syncRunId}] executeSync | isOnline=${this._context.params.isOnline} isStartUpSync=${this._context.params.isStartUpSync}`
    );
    window.logger.addBreadcrumb({
      message: `[SyncRegistry][${syncRunId}] executeSync | isOnline=${this._context.params.isOnline} isStartUpSync=${this._context.params.isStartUpSync}`,
    });
    this._trackerService.debugSyncPipelineStart(this._context.params.isOnline, this._context.params.isStartUpSync);
    debug('entered start function', this._context);
    for (const syncElement of this._syncElements) {
      this._trackerService.debugSyncPipelineElementStart(syncElement.name);
      try {
        debug(`[${syncRunId}] RUN ${syncElement.name}`);
        window.logger.addBreadcrumb({ message: `[SyncRegistry][${syncRunId}] RUN ${syncElement.name}` });
        this._currentSyncStep = syncElement.name;
        const result = await syncElement.execute(this._context);
        debug(`[${syncRunId}] DONE ${syncElement.name}`);
        this._trackerService.debugSyncPipelineElementDone(syncElement.name, result);
      } catch (error) {
        debug(`[${syncRunId}] ERROR ${syncElement.name}`, error?.message);
        this._trackerService.debugSyncPipelineElementNotExecuted(syncElement.name, 'error');
        // Normalized so Sentry never receives a bare null/undefined again (work item 27022).
        const stepError = toError(error, `Failed sync process for step: ${syncElement.name}`);
        window.logger.error(`Failed sync process for step: ${syncElement.name}`, stepError);
        reportedSyncErrors.add(stepError);
        throw stepError;
      }
    }
    debug(`[${syncRunId}] pipeline complete`);
    window.logger.addBreadcrumb({ message: `[SyncRegistry][${syncRunId}] pipeline complete` });
    this._trackerService.debugSyncPipelineDone();
  }

  public add(newSyncElement: ISyncElement, parent?: string): void {
    debug('adding new sync element', newSyncElement.name);
    if (parent) {
      const index = this._syncElements.findIndex(syncElement => syncElement.name === parent);
      this._syncElements.splice(index + 1, 0, newSyncElement);
    } else {
      this._syncElements.splice(0, 0, newSyncElement);
    }
  }

  private sortElementsByOrder(): void {
    const sortedSyncElements: ISyncElement[] = [];

    for (const endpoint of this._syncOrder) {
      const syncElement = this._syncElements.find(element => element.name === endpoint);
      if (!syncElement) {
        throw new Error(`Sync element for endpoint ${endpoint} is undefined.`);
      }

      sortedSyncElements.push(syncElement);
    }

    this._syncElements.length = 0;
    this._syncElements.push(...sortedSyncElements);
  }
}
